/**
 * Normalize a name for fuzzy matching by:
 * 1. Converting to lowercase
 * 2. Removing all non-alphanumeric characters (keeps only letters and numbers)
 *
 * Examples:
 *   "Test Corp" -> "testcorp"
 *   "O'Reilly & Associates" -> "oreillyassociates"
 *   "Amazon-US" -> "amazonus"
 */
export function normalizeName(name: string): string {
  if (!name) {
    return "";
  }

  // Remove all non-alphanumeric characters and convert to lowercase
  return name.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
}

/**
 * Check if two names match exactly (case-sensitive, after removing all whitespace).
 *
 * Examples:
 *   "Test Corp" vs "TestCorp" -> true
 *   "Test Corp" vs "test corp" -> false (case sensitive)
 *   "Test  Corp" vs "Test Corp" -> true
 */
export function isExactMatch(name1: string, name2: string): boolean {
  if (!name1 || !name2) {
    return false;
  }

  // Remove all whitespace and compare case-sensitively
  const name1NoSpace = name1.replace(/\s+/g, "");
  const name2NoSpace = name2.replace(/\s+/g, "");

  return name1NoSpace === name2NoSpace;
}

/**
 * Check if two names match after normalization (fuzzy match).
 * This strips non-alphanumeric characters and compares case-insensitively.
 */
export function isFuzzyMatch(name1: string, name2: string): boolean {
  if (!name1 || !name2) {
    return false;
  }

  const normalized1 = normalizeName(name1);
  const normalized2 = normalizeName(name2);

  // Need some characters to match on
  if (!normalized1 || !normalized2) {
    return false;
  }

  return normalized1 === normalized2;
}

export interface NameMatch<T extends { id: number | string; name: string }> {
  id: T["id"];
  name: string;
  originalItem: T;
}

export interface DuplicateCheckResult<T extends { id: number | string; name: string }> {
  exactMatch?: NameMatch<T>;
  fuzzyMatches: NameMatch<T>[];
}

/**
 * Check if a name has exact or fuzzy matches in a list of existing items.
 *
 * @param newName - The new name to check
 * @param existingItems - Array of items with at least { id, name } properties
 * @param excludeId - Optional ID to exclude from the check (e.g., when updating)
 * @returns Object with exactMatch (if found) and array of fuzzyMatches
 */
export function findMatchingNames<T extends { id: number | string; name: string }>(
  newName: string,
  existingItems: T[],
  excludeId?: number | string,
): DuplicateCheckResult<T> {
  let exactMatch: NameMatch<T> | undefined;
  const fuzzyMatches: NameMatch<T>[] = [];

  for (const item of existingItems) {
    // Skip if this is the record being updated
    if (excludeId !== undefined && item.id === excludeId) {
      continue;
    }

    // Check for exact match first
    if (isExactMatch(newName, item.name)) {
      exactMatch = {
        id: item.id,
        name: item.name,
        originalItem: item,
      };
      // Don't continue checking, exact match is most important
      break;
    }

    // Check for fuzzy match
    if (isFuzzyMatch(newName, item.name)) {
      fuzzyMatches.push({
        id: item.id,
        name: item.name,
        originalItem: item,
      });
    }
  }

  return {
    exactMatch,
    fuzzyMatches,
  };
}
